import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, openPlannerModello } from "@/lib/clinic";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, Upload, Download } from "lucide-react";

export const ExcelImportDialog = ({ open, onOpenChange, onImported }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import-excel", fd);
      setResult(data);
      toast.success(`${data.inserted} pazienti importati`);
      onImported?.(data.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Importazione fallita");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => openPlannerModello();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="excel-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Carica Planner Visite</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-600">
          Formato "Planner Visite": vengono lette le colonne{" "}
          <span className="font-medium text-slate-900">TIPO VISITA, MANSIONE, AZIENDA, DIPENDENTE, CF, EMAIL</span> e la data
          del planner. Con almeno una X (o valore) tra <span className="font-medium text-slate-900">A, D, P, U, C, T, ECG, SPIRO</span>{" "}
          il paziente passa obbligatoriamente per la Sala Jenner, altrimenti va direttamente in Sala Levi Montalcini.
          Gli orari del planner non vengono importati. Formati: .xlsx, .xls, .csv
        </p>

        <button
          type="button"
          data-testid="excel-file-dropzone"
          onClick={() => inputRef.current?.click()}
          className="w-full rounded-xl border-2 border-dashed border-slate-300 hover:border-sky-500 hover:bg-sky-50/60 transition-colors py-10 flex flex-col items-center gap-3"
        >
          <FileSpreadsheet className="w-8 h-8 text-sky-600" />
          <span className="text-sm font-medium text-slate-700">
            {file ? file.name : "Clicca per selezionare il file"}
          </span>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          data-testid="excel-file-input"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />

        {result && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1" data-testid="excel-import-result">
            <div className="font-medium text-emerald-700">{result.inserted} pazienti inseriti</div>
            {result.data && <div className="text-slate-600 text-xs">Data planner: {result.data}</div>}
            {result.skipped > 0 && <div className="text-slate-500 text-xs">{result.skipped} righe vuote ignorate</div>}
            {result.duplicati > 0 && (
              <div className="text-amber-700 text-xs">{result.duplicati} già presenti (non duplicati)</div>
            )}
            {result.errors?.slice(0, 5).map((e, i) => (
              <div key={i} className="text-slate-500 text-xs">{e}</div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={downloadTemplate} data-testid="excel-download-template-btn" className="rounded-full">
            <Download className="w-4 h-4 mr-2" /> Modello
          </Button>
          <Button onClick={upload} disabled={!file || busy} data-testid="excel-confirm-import-btn" className="rounded-full px-6">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
